/**
 * スポット価格のアコーディオン開閉状態 (in-memory 形状)。
 *
 * 画面には 3 つのアコーディオンがある。
 *
 * | value | 見出し |
 * |---|---|
 * | `chart` | 受渡日の約定価格 |
 * | `stats` | 集約表 |
 * | `koma` | コマ別エリア価格 |
 *
 * ## なぜフィルタと別に持つか
 *
 * `models/persistedFilters.ts` は AC-SC-031 [P0] が定める **フィルタバーの選択状態**
 * が対象で、開閉状態はフィルタではない。同じ画面・同じ復元タイミングだが概念が
 * 違うので key を分ける (書き戻しの契機も独立する)。読み書きのタイミングは共通
 * hook `~/hooks/usePersistedFilters` を再利用する。
 */

/** アコーディオンの識別子。`index.tsx` の `AccordionItem value` と 1:1。 */
export type SpotPriceSectionId = 'chart' | 'stats' | 'koma'

const VALID_SECTION_IDS: readonly SpotPriceSectionId[] = [
  'chart',
  'stats',
  'koma',
]

const isSectionId = (value: string): value is SpotPriceSectionId =>
  (VALID_SECTION_IDS as readonly string[]).includes(value)

/**
 * 初期表示で開いているセクション。**「受渡日の約定価格」だけ**。
 *
 * 2026-09-04 デザイナー判断。以前は `chart` + `koma` の 2 つを開いており
 * (design `bidding/spot-price.html` の `details[open]` 準拠)、下 2 つの
 * アコーディオンが画面外に押し出されて**存在に気付けなかった**。1 つだけ開いて
 * おけば、閉じた見出しが 2 本並ぶので「他にも中身がある」ことが読み取れる。
 *
 * [design差異] 8080 は `#sm-chart-accordion` と `#sm-koma-accordion` の 2 つに
 * `open` を付けている。本変更で実装が先行するため、プロト側も追随させる。
 */
export const DEFAULT_OPEN_SECTIONS: readonly SpotPriceSectionId[] = ['chart']

/**
 * 保存文字列から復元する。未保存・不正値は既定へ落とす。
 *
 * **空配列は「全部閉じた」という正当な状態**なので既定へ落とさない。利用者が
 * 3 つとも閉じたなら、戻った時も閉じているのが期待どおり。
 */
export function deserializeSpotPriceSections(
  raw: string | undefined,
): SpotPriceSectionId[] {
  if (raw === undefined) return [...DEFAULT_OPEN_SECTIONS]
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return [...DEFAULT_OPEN_SECTIONS]
    // 未知の id (旧版の残骸・手で書き換えた値) は捨てる。捨てた結果が空でも
    // それは「全部閉じた」と区別できないが、既知 id が 1 つも無い保存値は
    // そもそも壊れているので既定へ戻す方が安全。
    const known = parsed.filter(
      (v): v is SpotPriceSectionId => typeof v === 'string' && isSectionId(v),
    )
    if (known.length !== parsed.length && known.length === 0) {
      return [...DEFAULT_OPEN_SECTIONS]
    }
    return Array.from(new Set(known))
  } catch {
    return [...DEFAULT_OPEN_SECTIONS]
  }
}

/** state を保存文字列へ直列化する。 */
export function serializeSpotPriceSections(
  value: readonly SpotPriceSectionId[],
): string {
  return JSON.stringify(value)
}
